/**
 * Export utilities for generating PDF and Excel files
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Export package data to PDF
 */
export const exportPackageToPDF = (packageData) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(`${packageData.name} - Umrah Package`, pageWidth / 2, 20, {
      align: 'center',
    });

    // Add logo/header space
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(1);
    doc.line(10, 25, pageWidth - 10, 25);

    let yPos = 35;

    // Package Details Section
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Package Details', 15, yPos);
    yPos += 8;

    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);

    const details = [
      [`Package Name:`, packageData.name],
      [`Duration:`, `${packageData.duration} days`],
      [`Base Price:`, `$${packageData.basePrice?.toLocaleString() || '0'}`],
      [`City Focus:`, `${packageData.nightsInMakkah || 0} nights Makkah, ${packageData.nightsInMadina || 0} nights Madina`],
      [`Hotel:`, packageData.hotel?.name || 'N/A'],
      [`Rooms:`, packageData.hotel?.numberOfRooms || 'N/A'],
      [`Capacity:`, `${packageData.minGuests || 1} - ${packageData.maxGuests || 50} guests`],
      [`Tour Operator:`, packageData.tourOperator || 'N/A'],
    ];

    details.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, 15, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(value, 60, yPos);
      yPos += 6;
    });

    // Itinerary Section
    if (packageData.itinerary && packageData.itinerary.length > 0) {
      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Itinerary', 15, yPos);
      yPos += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      packageData.itinerary.forEach((day, idx) => {
        doc.setFont(undefined, 'bold');
        doc.text(`Day ${day.dayNumber || idx + 1}:`, 15, yPos);
        yPos += 5;

        doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(day.description || '', pageWidth - 30);
        lines.forEach((line) => {
          doc.text(line, 20, yPos);
          yPos += 4;
        });
        yPos += 2;

        // Check if we need a new page
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
      });
    }

    // Inclusions Section
    if (packageData.inclusions && packageData.inclusions.length > 0) {
      yPos += 8;
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Inclusions', 15, yPos);
      yPos += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      packageData.inclusions.forEach((inclusion) => {
        doc.text(`• ${inclusion.title || inclusion}`, 20, yPos);
        yPos += 5;
      });
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} | Umrah Booking Platform`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    doc.save(`${packageData.name.replace(/\s+/g, '_')}_Package.pdf`);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
};

/**
 * Export multiple packages to Excel
 */
export const exportPackagesToExcel = (packages, filename = 'Packages.xlsx') => {
  try {
    const data = packages.map((pkg) => ({
      'Package Name': pkg.name,
      'Duration (days)': pkg.duration,
      'Base Price ($)': pkg.basePrice,
      'Min Guests': pkg.minGuests,
      'Max Guests': pkg.maxGuests,
      'Nights in Makkah': pkg.nightsInMakkah,
      'Nights in Madina': pkg.nightsInMadina,
      'Hotel': pkg.hotel?.name || 'N/A',
      'Hotel Rooms': pkg.hotel?.numberOfRooms || 'N/A',
      'Tour Operator': pkg.tourOperator || 'N/A',
      'Rating': pkg.rating || pkg.averageRating || 'N/A',
      'Status': pkg.isActive ? 'Active' : 'Inactive',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Packages');

    // Auto-resize columns
    const maxWidth = 20;
    const colWidths = Object.keys(data[0] || {}).map((col) =>
      Math.min(
        maxWidth,
        Math.max(col.length, ...(data.map((row) => (row[col]?.toString() || '').length) || []))
      )
    );

    worksheet['!cols'] = colWidths.map((width) => ({ wch: width }));

    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting Excel:', error);
    alert('Error generating Excel file. Please try again.');
  }
};

/**
 * Export bookings to Excel
 */
export const exportBookingsToExcel = (bookings, filename = 'Bookings.xlsx') => {
  try {
    const data = bookings.map((booking) => ({
      'Booking ID': booking.id,
      'Package': booking.package?.name || 'N/A',
      'Guest Name': booking.guestName,
      'Email': booking.email,
      'Phone': booking.phone,
      'Travelers': booking.numberOfTravelers,
      'Total Price ($)': booking.totalPrice,
      'Status': booking.status,
      'Booking Date': new Date(booking.createdAt).toLocaleDateString(),
      'Travel Date': booking.travelDate ? new Date(booking.travelDate).toLocaleDateString() : 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');

    // Auto-resize columns
    const maxWidth = 20;
    const colWidths = Object.keys(data[0] || {}).map((col) =>
      Math.min(
        maxWidth,
        Math.max(col.length, ...(data.map((row) => (row[col]?.toString() || '').length) || []))
      )
    );

    worksheet['!cols'] = colWidths.map((width) => ({ wch: width }));

    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting Excel:', error);
    alert('Error generating Excel file. Please try again.');
  }
};

/**
 * Generate CSV from array of objects
 */
export const generateCSV = (data, filename = 'export.csv') => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV file. Please try again.');
  }
};
